import React, { useState, useEffect } from 'react'
import config from '../../config.js'
import { formatCurrency } from './formatters'

const NESTER = config.NESTER_API_URL

/**
 * Compact customer-history card surfaced on the Project Dashboard when a
 * customer is assigned to the project. Shows win rate, top material, and
 * recent project list so the quoter can see what this customer usually buys
 * at a glance.
 */
export default function NesterCustomerHistory({ colors, customerId, customerName }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!customerId) { setData(null); return }
    let cancelled = false
    setLoading(true); setError(null)
    fetch(`${NESTER}/customers/${customerId}/nester_history`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`${r.status}`)))
      .then(d => { if (!cancelled) setData(d) })
      .catch(e => { if (!cancelled) setError(e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [customerId])

  if (!customerId) return null

  return (
    <div style={{
      padding: '10px 14px', marginBottom: 14,
      background: colors.panel,
      border: `1px solid ${colors.border}`,
      borderRadius: 6,
      boxShadow: colors.shadow,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 16 }}>👤</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.text }}>
            Customer History — {customerName || `#${customerId}`}
          </div>
          <div style={{ fontSize: 11, color: colors.textMuted }}>
            Prior quotes, wins, and preferred materials
          </div>
        </div>
      </div>

      {loading && <div style={{ fontSize: 12, color: colors.textMuted }}>Loading…</div>}
      {error && <div style={{ fontSize: 12, color: colors.warn }}>Could not load: {error}</div>}

      {data && (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
            gap: 6, marginBottom: 10,
          }}>
            <Stat colors={colors} label="Projects" value={data.summary.nester_project_count} />
            <Stat colors={colors} label="Wins" value={`${data.summary.nester_won_count} (${data.summary.win_rate_pct}%)`} />
            <Stat colors={colors} label="Quoted" value={formatCurrency(data.summary.total_quoted_dollars)} />
            <Stat colors={colors} label="Sold" value={formatCurrency(data.summary.total_sales_order_dollars)} />
            <Stat colors={colors} label="Top material" value={data.summary.top_material || '—'} />
          </div>

          {data.nester_projects.length > 0 && (
            <div>
              <div style={{
                fontSize: 10, fontWeight: 700, color: colors.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
              }}>
                Recent nester projects
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {data.nester_projects.slice(0, 5).map(p => (
                  <div key={p.project_id} style={{
                    display: 'flex', gap: 8, fontSize: 12, color: colors.text,
                    padding: '3px 6px', borderRadius: 3,
                  }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.project_name}
                    </span>
                    <span style={{ color: colors.textMuted, fontSize: 11 }}>
                      {p.material_type || '—'} {p.thickness_display || ''}
                    </span>
                    <StatusDot colors={colors} status={p.status} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Stat({ colors, label, value }) {
  return (
    <div style={{
      padding: '5px 8px',
      background: colors.panelAlt,
      border: `1px solid ${colors.border}`,
      borderRadius: 4,
    }}>
      <div style={{
        fontSize: 9, fontWeight: 700, color: colors.textMuted,
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {label}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: colors.text, marginTop: 1 }}>
        {value}
      </div>
    </div>
  )
}

function StatusDot({ colors, status }) {
  const map = {
    draft: colors.textMuted,
    quoted: colors.accent,
    won: colors.success,
    lost: colors.danger,
  }
  return (
    <span style={{
      fontSize: 10, fontWeight: 700,
      padding: '1px 6px', borderRadius: 8,
      color: map[status] || colors.textMuted,
      border: `1px solid ${map[status] || colors.border}`,
    }}>
      {status}
    </span>
  )
}
